import { useContext, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FilterFieldType } from 'constants/filters/filterFieldType';
import { ParamsCtx } from '../paramsCtx';
import { hasValue, isString } from 'utils/helper/type';

export function useFilterForm({ fields } = {}) {
  const { values, filterFields, removeFilter, setFilter } = useContext(ParamsCtx);
  const formFields = fields || filterFields;
  const { allFields, fieldsMap } = useMemo(
    () =>
      Object.values(formFields).reduce(
        (acc, field) => {
          acc.allFields.push(field.key);
          acc.fieldsMap[field.key] = field;

          return acc;
        },
        { allFields: [], fieldsMap: {} }
      ),
    [formFields]
  );
  const formProps = useForm({ mode: 'all' });
  const valuesArr = useMemo(() => allFields.map((field) => values[field]), [allFields, values]);

  const reset = formProps.reset;
  const defaults = useMemo(
    () =>
      allFields.reduce((acc, field, index) => {
        const type = fieldsMap[field].type;

        if (hasValue(valuesArr[index])) {
          acc[field] = valuesArr[index];
        } else {
          if (type === FilterFieldType.TEXT || !type) {
            acc[field] = '';
          } else if (field === FilterFieldType.TOGGLE) {
            acc[field] = false;
          } else {
            acc[field] = null;
          }
        }
        return acc;
      }, {}),
    [fieldsMap, allFields, ...valuesArr] // eslint-disable-line
  );

  useEffect(() => {
    reset(defaults);
  }, [defaults, reset]);

  const onSubmit = formProps.handleSubmit((data) => setFilter(data, formFields));
  const onReset = (key) => {
    reset(defaults);
    removeFilter(isString(key) ? [key] : allFields);
  };

  const formValues = useMemo(
    () =>
      allFields.reduce((acc, field) => {
        acc[field] = values[field];
        return acc;
      }, {}),
    [allFields, values]
  );
  const hasFilter = useMemo(() => allFields.some((field) => hasValue(formValues[field])), [allFields, formValues]);

  return { onSubmit, hasFilter, onReset, ...formProps, values: formValues, filterFields };
}
